import { useState, useEffect } from"react";
import axios from"axios";
import { useTranslation } from"react-i18next";
import Layout from"@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from"@/components/ui/card";
import { Button } from"@/components/ui/button";
import { Input } from"@/components/ui/input";
import { Label } from"@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from"@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from"@/components/ui/select";
import { Badge } from"@/components/ui/badge";
import { Plus, Building2, Trash2, Pencil, Check, X, BarChart3 } from"lucide-react";
import { toast } from"sonner";
import { useNavigate } from"react-router-dom";
import EntityDocumentsTab from"@/components/documents/EntityDocumentsTab";

const ProjectsPage = ({ user, onLogout }) => {
 const { t } = useTranslation();
 const navigate = useNavigate();
 const [projects, setProjects] = useState([]);
 const [engineers, setEngineers] = useState([]);
 const [buildings, setBuildings] = useState([]);
 const [projectProgress, setProjectProgress] = useState({});
 const [loading, setLoading] = useState(true);
 const [dialogOpen, setDialogOpen] = useState(false);
 const [detailOpen, setDetailOpen] = useState(false);
 const [buildingDialogOpen, setBuildingDialogOpen] = useState(false);
 const [selectedProject, setSelectedProject] = useState(null);
 const [formData, setFormData] = useState({ name:"", code:"", city:"", status:"active"});
 const [buildingFormData, setBuildingFormData] = useState({ name:"", floors: 1 });
 const [editingProjectName, setEditingProjectName] = useState(false);
 const [editProjectName, setEditProjectName] = useState("");
 const [editingBuildingId, setEditingBuildingId] = useState(null);
 const [editBuildingName, setEditBuildingName] = useState("");
 const [editBuildingFloors, setEditBuildingFloors] = useState(1);

 useEffect(() => { loadProjects(); loadEngineers(); }, []);

 const calculateWeightedProgress = (jobs) => {
 // Phase weights based on construction importance
 const phaseWeights = {
 1: 10, // Documentation
 2: 40, // Structural & Masonry Works
 3: 40, // Installations & Finishing Works
 4: 10 // Finalization & Legalization
 };
 
 // Group jobs by phase
 const phaseGroups = {
 1: jobs.filter(j => j.phase === 1),
 2: jobs.filter(j => j.phase === 2),
 3: jobs.filter(j => j.phase === 3),
 4: jobs.filter(j => j.phase === 4)
 };
 
 let totalWeightedProgress = 0;
 let phaseDetails = [];
 
 // Calculate weighted progress for each phase
 for (const [phase, weight] of Object.entries(phaseWeights)) {
 const phaseJobs = phaseGroups[phase] || [];
 const phaseTotal = phaseJobs.length;
 const phaseCompleted = phaseJobs.filter(j => j.status === 'completed').length;
 
 if (phaseTotal > 0) {
 const phaseCompletion = (phaseCompleted / phaseTotal) * 100;
 const weightedContribution = (phaseCompletion * weight) / 100;
 totalWeightedProgress += weightedContribution;
 
 phaseDetails.push({
 phase: parseInt(phase),
 weight,
 completed: phaseCompleted,
 total: phaseTotal,
 percentage: Math.round(phaseCompletion)
 });
 }
 }
 
 return {
 percentage: Math.round(totalWeightedProgress),
 completed: jobs.filter(j => j.status === 'completed').length,
 total: jobs.length,
 phaseDetails
 };
 };

 const loadProjects = async () => {
 try {
 const response = await axios.get("/projects");
 const projectsData = response.data;
 setProjects(projectsData);
 
 // Load progress for each project with weighted calculation
 const progressMap = {};
 for (const project of projectsData) {
 try {
 const progressRes = await axios.get(`/project-progress/${project.id}`);
 const jobs = progressRes.data || [];
 progressMap[project.id] = calculateWeightedProgress(jobs);
 } catch (err) {
 progressMap[project.id] = { 
 percentage: 0, 
 completed: 0, 
 total: 0, 
 phaseDetails: [] 
 };
 }
 }
 setProjectProgress(progressMap);
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 } finally {
 setLoading(false);
 }
 };

 const loadEngineers = async () => {
 try {
 const response = await axios.get("/users");
 setEngineers(response.data.filter(u => u.role ==="engineer"));
 } catch (error) {
 console.error("Failed to load engineers", error);
 }
 };

 const handleSubmit = async (e) => {
 e.preventDefault();
 try {
 await axios.post("/projects", formData);
 toast.success(t("messages.saveSuccess"));
 setDialogOpen(false);
 setFormData({ name:"", code:"", city:"", status:"active"});
 loadProjects();
 } catch (error) {
 toast.error(error.response?.data?.detail ||"Failed to create project");
 }
 };

 const handleAssignEngineer = async (engineerId) => {
 try {
 await axios.patch(`/projects/${selectedProject.id}/assign-engineer?engineer_id=${engineerId}`);
 toast.success(t("messages.saveSuccess"));
 await loadProjects();
 await loadEngineers(); // Reload engineers to refresh the list
 // Update selected project
 setSelectedProject({...selectedProject, assigned_engineer_id: engineerId});
 } catch (error) {
 toast.error(error.response?.data?.detail ||"Failed to assign engineer");
 }
 };

 const loadBuildingsForProject = async (projectId) => {
 try {
 const response = await axios.get(`/buildings?project_id=${projectId}`);
 setBuildings(response.data);
 } catch (error) {
 console.error("Failed to load buildings", error);
 }
 };

 const handleCreateBuilding = async (e) => {
 e.preventDefault();
 try {
 await axios.post("/buildings", {
 ...buildingFormData,
 project_id: selectedProject.id
 });
 toast.success(t("messages.saveSuccess"));
 setBuildingDialogOpen(false);
 setBuildingFormData({ name:"", floors: 1 });
 loadBuildingsForProject(selectedProject.id);
 } catch (error) {
 toast.error(error.response?.data?.detail ||"Failed to create building");
 }
 };

 const handleDeleteProject = async (projectId) => {
 if (!window.confirm("Are you sure you want to delete this project? This cannot be undone.")) {
 return;
 }
 try {
 await axios.delete(`/projects/${projectId}`);
 toast.success(t("messages.deleteSuccess"));
 setDetailOpen(false);
 loadProjects();
 } catch (error) {
 if (error.response?.status === 409) {
 toast.error(error.response.data.detail ||"Project has linked data and cannot be deleted");
 } else {
 toast.error(t("messages.loadFailed"));
 }
 }
 };

 const handleUpdateProjectName = async () => {
 if (!editProjectName.trim()) {
 toast.error(t("validation.required"));
 return;
 }
 try {
 await axios.patch(`/projects/${selectedProject.id}`, { name: editProjectName });
 toast.success(t("messages.updateSuccess"));
 setEditingProjectName(false);
 setSelectedProject({ ...selectedProject, name: editProjectName });
 loadProjects();
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 }
 };

 const handleUpdateBuildingName = async (buildingId) => {
 if (!editBuildingName.trim()) {
 toast.error(t("validation.required"));
 return;
 }
 if (editBuildingFloors < 1) {
 toast.error(t("validation.required"));
 return;
 }
 try {
 await axios.patch(`/buildings/${buildingId}`, { 
 name: editBuildingName,
 floors: editBuildingFloors 
 });
 toast.success(t("messages.updateSuccess"));
 setEditingBuildingId(null);
 loadBuildingsForProject(selectedProject.id);
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 }
 };

 const handleUnassignEngineer = async () => {
 if (!window.confirm("Are you sure you want to remove the assigned engineer?")) {
 return;
 }
 try {
 await axios.patch(`/projects/${selectedProject.id}/unassign-engineer`);
 toast.success(t("messages.deleteSuccess"));
 await loadProjects();
 setSelectedProject({...selectedProject, assigned_engineer_id: null});
 } catch (error) {
 toast.error(error.response?.data?.detail ||"Failed to remove engineer");
 }
 };

 const statusColors = { active:"bg-emerald-500/10 text-emerald-700 border-emerald-200/60", planning:"bg-blue-500/10 text-blue-700 border-blue-200/60", completed:"bg-gray-500/10 text-gray-700 border-slate-200", on_hold:"bg-amber-500/10 text-amber-700 border-amber-200/60"};

 return (
 <Layout user={user} onLogout={onLogout}>
 <div data-testid="projects-page" className="space-y-8">
 <div className="flex items-center justify-between">
 <div>
 <h1 className="font-display text-3xl font-bold text-slate-900 tracking-tight uppercase">{t('projects.title')}</h1>
 <p className="text-sm text-slate-500 mt-1">{t('projects.subtitle')}</p>
 </div>
 {["owner","management"].includes(user.role) && (
 <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
 <DialogTrigger asChild>
 <Button data-testid="create-project-button"className="bg-slate-900 hover:bg-slate-800:bg-zinc-200 text-sm"><Plus className="w-4 h-4 mr-2"/>{t('projects.newProject')}</Button>
 </DialogTrigger>
 <DialogContent data-testid="create-project-dialog">
 <DialogHeader><DialogTitle>{t('projects.newProject')}</DialogTitle></DialogHeader>
 <form onSubmit={handleSubmit} className="space-y-4">
 <div>
 <Label htmlFor="name">{t('projects.projectName')}</Label>
 <Input id="name"data-testid="project-name-input"value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
 </div>
 <div>
 <Label htmlFor="code">{t('projects.projectCode')}</Label>
 <Input id="code"data-testid="project-code-input"value={formData.code} onChange={(e) => setFormData({ ...formData, code: e.target.value })} placeholder="e.g., SKP001"required />
 </div>
 <div>
 <Label htmlFor="city">{t('common.city')}</Label>
 <Input id="city"data-testid="project-city-input"value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} required />
 </div>
 <div>
 <Label htmlFor="status">{t('common.status')}</Label>
 <Select value={formData.status} onValueChange={(val) => setFormData({ ...formData, status: val })}>
 <SelectTrigger data-testid="project-status-select"><SelectValue /></SelectTrigger>
 <SelectContent>
 <SelectItem value="planning">{t('projectStatus.planning')}</SelectItem>
 <SelectItem value="active">{t('common.active')}</SelectItem>
 <SelectItem value="on_hold">{t('projectStatus.onHold')}</SelectItem>
 <SelectItem value="completed">{t('common.completed')}</SelectItem>
 </SelectContent>
 </Select>
 </div>
 <Button type="submit"data-testid="submit-project-button"className="w-full bg-slate-900 hover:bg-slate-800:bg-zinc-200">{t('projects.newProject')}</Button>
 </form>
 </DialogContent>
 </Dialog>
 )}
 </div>
 {loading ? (
 <div className="flex items-center justify-center h-64"><div className="text-sm text-slate-500">{t('common.loading')}</div></div>
 ) : (
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
 {projects.map((project) => (
 <Card 
 key={project.id} 
 data-testid={`project-card-${project.id}`} 
 className={`bg-white border border-slate-200 rounded-sm hover:shadow-md transition-shadow cursor-pointer ${
 project.status === 'completed' ? 'border-emerald-200/60/80' : ''
 }`}
 onClick={() => { 
 setSelectedProject(project); 
 setDetailOpen(true); 
 loadEngineers(); 
 loadBuildingsForProject(project.id); 
 }}
 >
 <CardHeader>
 <div className="flex items-start justify-between">
 <div className="flex items-center gap-3">
 <Building2 className="h-7 w-7 text-indigo-600"/>
 <div>
 <CardTitle className="text-lg">{project.name}</CardTitle>
 <p className="text-sm text-slate-500">{project.code}</p>
 </div>
 </div>
 <Badge variant="outline"className={statusColors[project.status]}>{project.status.replace('_', ' ')}</Badge>
 </div>
 </CardHeader>
 <CardContent>
 <div className="space-y-3">
 <div className="flex items-center justify-between text-sm"><span className="text-sm text-slate-500">{t('common.city')}:</span><span className="font-medium text-slate-900">{project.city}</span></div>
 <div className="flex items-center justify-between text-sm"><span className="text-sm text-slate-500">{t('projects.created')}:</span><span className="text-slate-900">{new Date(project.created_at).toLocaleDateString()}</span></div>
 
 {/* Progress Bar with Phase Weights (hide for completed projects) */}
 {project.status !== 'completed' && projectProgress[project.id] ? (
 <div className="pt-2 border-t border-slate-200">
 <div className="flex items-center justify-between text-xs mb-1">
 <span className="text-slate-600 font-medium">{t('projects.constructionProgress')}</span>
 <span className="text-slate-900 font-bold">{projectProgress[project.id].percentage}%</span>
 </div>
 <div className="w-full bg-white/60 rounded-full h-1.5 overflow-hidden">
 <div
 className="h-full rounded-full transition-all duration-500 bg-slate-900"
 style={{ width: `${projectProgress[project.id].percentage}%` }}
 />
 </div>
 <div className="flex items-center justify-between text-xs mt-1.5 text-slate-500">
 <span>{projectProgress[project.id].completed} {t('projects.ofTasks', { total: projectProgress[project.id].total })}</span>
 <span className="text-blue-600 font-medium"title={t('projects.phaseWeightsTooltip')}>ⓘ {t('projects.weighted')}</span>
 </div>
 </div>
 ) : null}
 </div>
 </CardContent>
 </Card>
 ))}
 </div>
 )}
 </div>
 {/* Project detail with Documents */}
 <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
 <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
 <DialogHeader>
 <div className="flex items-center justify-between pr-8">
 <div className="flex items-center gap-2 flex-1 min-w-0">
 {editingProjectName ? (
 <div className="flex items-center gap-2 flex-1">
 <Input
 value={editProjectName}
 onChange={(e) => setEditProjectName(e.target.value)}
 onKeyDown={(e) => {
 if (e.key === 'Enter') handleUpdateProjectName();
 if (e.key === 'Escape') setEditingProjectName(false);
 }}
 className="font-bold text-lg rounded-sm"
 autoFocus
 />
 <Button size="sm" className="rounded-sm" onClick={handleUpdateProjectName}>
 <Check className="w-4 h-4"/>
 </Button>
 <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setEditingProjectName(false)}>
 <X className="w-4 h-4"/>
 </Button>
 </div>
 ) : (
 <>
 <DialogTitle className="font-display text-xl font-bold uppercase tracking-tight text-slate-900 truncate">{selectedProject?.name}</DialogTitle>
 {(user.role ==="owner"|| user.role ==="management") && (
 <Button size="sm" variant="ghost" className="h-7 w-7 p-0 rounded-sm flex-shrink-0"
 onClick={() => { setEditingProjectName(true); setEditProjectName(selectedProject?.name ||""); }}>
 <Pencil className="w-3.5 h-3.5 text-slate-400"/>
 </Button>
 )}
 </>
 )}
 </div>
 <div className="flex gap-2 flex-shrink-0 ml-4">
 {selectedProject && (
 <Button size="sm" className="rounded-sm bg-slate-900 hover:bg-slate-800 text-xs"
 onClick={() => { navigate(`/projects/${selectedProject.id}/dashboard`); setDetailOpen(false); }}>
 <BarChart3 className="w-3.5 h-3.5 mr-1.5"/>
 Dashboard
 </Button>
 )}
 {user.role ==="owner"&& selectedProject && (
 <Button variant="destructive" size="sm" className="rounded-sm text-xs"
 onClick={() => handleDeleteProject(selectedProject.id)}
 data-testid="delete-project-button">
 <Trash2 className="w-3.5 h-3.5 mr-1.5"/>
 {t('common.delete')}
 </Button>
 )}
 </div>
 </div>
 </DialogHeader>
 
 <div className="space-y-6 mt-4">
 {/* Project Information */}
 <div className="space-y-3">
 <h3 className="text-sm font-semibold text-slate-900">{t('projects.projectInfo')}</h3>
 <div className="grid grid-cols-2 gap-4 text-sm">
 <div>
 <span className="text-sm text-slate-500">{t('projects.projectCode')}:</span>
 <span className="ml-2 font-medium text-slate-900">{selectedProject?.code}</span>
 </div>
 <div>
 <span className="text-sm text-slate-500">{t('common.city')}:</span>
 <span className="ml-2 font-medium text-slate-900">{selectedProject?.city}</span>
 </div>
 <div>
 <span className="text-sm text-slate-500">{t('common.status')}:</span>
 <Badge variant="outline"className={`ml-2 ${statusColors[selectedProject?.status]}`}>
 {selectedProject?.status?.replace('_', ' ')}
 </Badge>
 </div>
 <div>
 <span className="text-sm text-slate-500">{t('projects.created')}:</span>
 <span className="ml-2 text-slate-900">
 {selectedProject && new Date(selectedProject.created_at).toLocaleDateString()}
 </span>
 </div>
 </div>
 </div>

 {/* Engineer Assignment */}
 {(user.role ==="owner"|| user.role ==="management") && (
 <div className="space-y-3 border-t pt-4">
 <h3 className="text-sm font-semibold text-slate-900">{t('projects.responsibleEngineer')}</h3>
 <div className="flex gap-2">
 <Select 
 value={selectedProject?.assigned_engineer_id ||""} 
 onValueChange={handleAssignEngineer}
 className="flex-1"
 >
 <SelectTrigger>
 <SelectValue placeholder={t('projects.selectEngineer')} />
 </SelectTrigger>
 <SelectContent>
 {engineers.map((engineer) => (
 <SelectItem key={engineer.id} value={engineer.id}>
 {engineer.name} ({engineer.email})
 </SelectItem>
 ))}
 </SelectContent>
 </Select>
 {selectedProject?.assigned_engineer_id && (
 <Button 
 variant="outline"
 size="sm"
 onClick={handleUnassignEngineer}
 className="text-red-600 hover:text-red-700 hover:bg-red-50/80"
 >
 <X className="w-4 h-4 mr-1"/>
 {t('common.remove')}
 </Button>
 )}
 </div>
 {selectedProject?.assigned_engineer_id && (
 <p className="text-xs text-green-600">
 ✓ {t('projects.assigned')}: {engineers.find(e => e.id === selectedProject.assigned_engineer_id)?.name || t('common.unknown')}
 </p>
 )}
 </div>
 )}

 {/* Buildings */}
 <div className="space-y-3 border-t pt-4">
 <div className="flex items-center justify-between">
 <h3 className="text-sm font-semibold text-slate-900">{t('buildings.title')}</h3>
 {(user.role ==="owner"|| user.role ==="management") && (
 <Button onClick={() => setBuildingDialogOpen(true)} size="sm">
 <Plus className="w-4 h-4 mr-2"/>
 {t('buildings.addBuilding')}
 </Button>
 )}
 </div>
 
 {buildings.length > 0 ? (
 <div className="grid gap-2">
 {buildings.map((building) => (
 <div key={building.id} className="p-3 border border-slate-200 rounded-sm">
 {editingBuildingId === building.id ? (
 <div className="space-y-2">
 <div className="flex items-center gap-2">
 <Input
 value={editBuildingName}
 onChange={(e) => setEditBuildingName(e.target.value)}
 onKeyDown={(e) => {
 if (e.key === 'Enter') handleUpdateBuildingName(building.id);
 if (e.key === 'Escape') setEditingBuildingId(null);
 }}
 placeholder={t('buildings.buildingName')}
 className="flex-1"
 autoFocus
 />
 <Input
 type="number"
 min="1"
 value={editBuildingFloors}
 onChange={(e) => setEditBuildingFloors(parseInt(e.target.value) || 1)}
 onKeyDown={(e) => {
 if (e.key === 'Enter') handleUpdateBuildingName(building.id);
 if (e.key === 'Escape') setEditingBuildingId(null);
 }}
 placeholder={t('buildings.floors')}
 className="w-24"
 />
 <Button size="sm"onClick={() => handleUpdateBuildingName(building.id)}>
 <Check className="w-4 h-4"/>
 </Button>
 <Button size="sm"variant="outline"onClick={() => setEditingBuildingId(null)}>
 <X className="w-4 h-4"/>
 </Button>
 </div>
 <p className="text-sm text-slate-500 mt-1">
 {t('buildings.floorsTip')}
 </p>
 </div>
 ) : (
 <div className="flex items-center justify-between">
 <div>
 <div className="font-medium text-slate-900">{building.name}</div>
 <div className="text-sm text-slate-600">
 {building.floors} {t('buildings.floors')} ({t('buildings.ground')} + {building.floors - 1} {t('buildings.upper')})
 </div>
 </div>
 {(user.role ==="owner"|| user.role ==="management") && (
 <Button
 size="sm"
 variant="ghost"
 onClick={() => {
 setEditingBuildingId(building.id);
 setEditBuildingName(building.name);
 setEditBuildingFloors(building.floors);
 }}
 >
 <Pencil className="w-4 h-4"/>
 </Button>
 )}
 </div>
 )}
 </div>
 ))}
 </div>
 ) : (
 <div className="text-center p-8 border-2 border-dashed border-slate-200 rounded-sm">
 <p className="text-slate-500 mb-3">{t('buildings.noBuildings')}</p>
 {(user.role ==="owner"|| user.role ==="management") && (
 <Button onClick={() => setBuildingDialogOpen(true)} size="sm">
 {t('buildings.addFirstBuilding')}
 </Button>
 )}
 </div>
 )}
 </div>

 {/* Documents */}
 <div className="space-y-3 border-t pt-4">
 <h3 className="text-sm font-semibold text-slate-900">{t('documents.title')}</h3>
 {selectedProject && (
 <EntityDocumentsTab user={user} entityType="project"entityId={selectedProject.id} />
 )}
 </div>
 </div>
 </DialogContent>
 </Dialog>
 {/* Create Building Dialog */}
 <Dialog open={buildingDialogOpen} onOpenChange={setBuildingDialogOpen}>
 <DialogContent>
 <DialogHeader>
 <DialogTitle>{t('buildings.addBuildingTo')} {selectedProject?.name}</DialogTitle>
 </DialogHeader>
 <form onSubmit={handleCreateBuilding} className="space-y-4">
 <div>
 <Label htmlFor="building_name">{t('buildings.buildingName')}</Label>
 <Input
 id="building_name"
 value={buildingFormData.name}
 onChange={(e) => setBuildingFormData({ ...buildingFormData, name: e.target.value })}
 placeholder={t('buildings.buildingNamePlaceholder')}
 required
 />
 </div>
 <div>
 <Label htmlFor="building_floors">{t('buildings.numberOfFloors')}</Label>
 <Input
 id="building_floors"
 type="number"
 min="1"
 value={buildingFormData.floors}
 onChange={(e) => setBuildingFormData({ ...buildingFormData, floors: parseInt(e.target.value) })}
 placeholder="e.g., 10"
 required
 />
 </div>
 <Button type="submit"className="w-full">{t('buildings.createBuilding')}</Button>
 </form>
 </DialogContent>
 </Dialog>
 </Layout>
 );
};

export default ProjectsPage;
